/**
 * One district, under the cursor.
 *
 * Shows the active metric first — the thing the reader is currently comparing
 * districts by — then the context that never changes, then the A–G split of its
 * dwellings. The order matters: the choropleth answers "which districts", and
 * this card has to answer "how much, here" before anything else.
 */

import type { DistritoProps, Metric } from "./lib/distritos";
import {
  areaContext, areaName, letterShares, num, ratedDwellings,
} from "./lib/distritos";
import { fmt } from "./lib/format";
import { LETTER_COLORS, LETTER_TEXT } from "./lib/letters";
import type { Anchor } from "./HoverCard";
import { HoverCard } from "./HoverCard";

interface Props {
  anchor: Anchor | null;
  props: DistritoProps | null;
  /** Null when no choropleth is painted; the card then omits the value. */
  metric: Metric | null;
}

export function DistritoPopup({ anchor, props: p, metric }: Props) {
  if (!p) return null;

  const shares = letterShares(p);
  const rated = ratedDwellings(p);

  return (
    <HoverCard anchor={anchor} width={264} className="distrito-card">
      <h2 className="card-title">{areaName(p)}</h2>
      <p className="card-eyebrow">{areaContext(p)}</p>

      {/* Omitted rather than dashed when no metric is chosen: there is no
          figure to withhold, and '–' would read as "not measured here". */}
      {metric && (
        <p className="card-metric">
          <span className="card-metric-val">{metric.format(num(p[metric.key]))}</span>
          <span className="card-metric-lbl">{metric.label}</span>
        </p>
      )}

      <p className="card-foot">
        {fmt(num(p["total_edificios"]))} edificios ·{" "}
        {fmt(num(p["n_viviendas"]))} viviendas · renta{" "}
        <b className="nowrap">{fmt(num(p["renta_neta_hogar"]), 0, "€/hogar")}</b>
      </p>

      {shares.length > 0 && (
        <section className="card-block bare">
          <h3>% de viviendas por calificación</h3>
          {/* A stacked strip plus the exact share per rating, both weighted by
              dwellings — see letterShares for why buildings would mislead. */}
          <div className="letter-strip">
            {shares.map((s) => s.dwellings > 0 && (
              <span
                key={s.letter}
                style={{ background: LETTER_COLORS[s.letter], flex: s.dwellings }}
                title={`${s.letter}: ${fmt(s.dwellings)} viviendas`}
              />
            ))}
          </div>
          <div className="letter-cells">
            {shares.map((s) => (
              // Ratings with no dwellings stay in the grid but recede, so the
              // reader sees the letter is absent rather than wondering why the
              // row is short.
              <div key={s.letter} className={s.pct === 0 ? "letter-cell dim" : "letter-cell"}>
                <span
                  className="letter-tag"
                  style={{
                    background: LETTER_COLORS[s.letter],
                    color: LETTER_TEXT[s.letter],
                  }}
                >
                  {s.letter}
                </span>
                <span className="letter-pct">{s.pct.toFixed(1)}%</span>
              </div>
            ))}
          </div>
          <p className="card-small">sobre {fmt(rated)} viviendas con calificación</p>
        </section>
      )}
    </HoverCard>
  );
}
